import { useEffect } from 'react';
import { safeTrack } from '../../utils/analytics';
import {
  CartAddition,
  getCartTrackingData,
  clearCartTracking,
} from '../../utils/cart-tracking';

const Purchase: React.FC<{
  store: string;
  provider: string;
  customer: string;
}> = ({ store, provider, customer }) => {
  const getTotalOrderValue = (orderData: any) => {
    switch (provider) {
      case 'salla':
        return orderData.revenue;
      case 'zid': {
        const hasVitrin = window.vitrin == true;
        if (!hasVitrin) {
          let total = 0;
          orderData.forEach((item: any) => {
            total += item.total;
          });
          return total;
        } else {
          return Number(orderData.order.order_total);
        }
      }
      default:
        return 0;
    }
  };

  const normalizeTrackingData = (cartAddition: CartAddition) => {
    const { source, pageType, recommendationType } = cartAddition;

    switch (source) {
      case 'search_bar':
        return {
          source: 'search',
          type: 'search_bar',
        };

      case 'search_page':
        return {
          source: 'search',
          type: 'search_page',
        };

      case 'recommendations':
        return {
          source: 'recommendations',
          type: recommendationType,
          page: pageType,
        };

      case 'frequently_bought_together':
        return {
          source: 'frequently_bought_together',
          type: 'product_details_page',
        };

      case 'frequently_bought_together_modal':
        return {
          source: 'frequently_bought_together',
          type: 'modal',
        };

      default:
        return {
          source: 'unknown',
          type: 'unknown',
        };
    }
  };

  const getProducts = (orderData: any) => {
    // Get cart additions from localStorage
    const cartAdditions: CartAddition[] = getCartTrackingData();

    const enrichProductWithTracking = (product: {
      name: string;
      id: string | number;
      quantity: number;
      price: number;
    }) => {
      // Find the first cart addition for this product (first-touch attribution)
      const firstAddition = cartAdditions.find(
        (addition) => addition.productName === product.name,
      );

      // Determine if this product was tracked by Alfinder
      const isAlfinderProduct = !!firstAddition;

      return {
        ...product,
        alfinder_source: firstAddition?.source || null, // First source that added this product
        is_alfinder_product: isAlfinderProduct, // Boolean flag for easy filtering
        // Include recommendation metadata if source is recommendations
        ...(firstAddition?.source === 'recommendations' && {
          pageType: firstAddition.pageType,
          recommendationType: firstAddition.recommendationType,
        }),
      };
    };

    switch (provider) {
      case 'salla':
        return orderData.products.map((product: any) =>
          enrichProductWithTracking({
            name: product.name,
            id: product.id,
            quantity: product.quantity,
            price: product.price || 0,
          }),
        );
      case 'zid':
        const hasVitrin = window.vitrin == true;
        return !hasVitrin
          ? orderData.map((item: any) =>
              enrichProductWithTracking({
                name: item.name,
                id: item.product_id,
                quantity: item.quantity,
                price: item.price || 0,
              }),
            )
          : orderData.items.map((item: any) =>
              enrichProductWithTracking({
                name: item.item_name,
                id: item.item_id,
                quantity: item.quantity,
                price: item.price || 0,
              }),
            );
    }
  };

  const getProductsNew = (orderData: any) => {
    // Get cart additions from localStorage
    const cartAdditions: CartAddition[] = getCartTrackingData();

    const enrichProductWithTrackingNew = (product: {
      name: string;
      id: string | number;
      quantity: number;
      price: number;
    }) => {
      // Find the first cart addition for this product (first-touch attribution)
      const firstAddition = cartAdditions.find(
        (addition) => addition.productName === product.name,
      );

      // Determine if this product was tracked by Alfinder

      // Normalize tracking data to generic format
      const normalizedTracking = firstAddition
        ? normalizeTrackingData(firstAddition)
        : null;

      return {
        ...product,
        // Generic normalized tracking data
        ...(normalizedTracking && {
          source: normalizedTracking.source,
          type: normalizedTracking.type,
          ...(normalizedTracking.page && { page: normalizedTracking.page }),
        }),
      };
    };

    switch (provider) {
      case 'salla':
        return orderData.products.map((product: any) =>
          enrichProductWithTrackingNew({
            ...product,
            name: product.name,
            id: product.id,
            quantity: product.quantity,
            price: product.price || 0,
          }),
        );
      case 'zid':
        const hasVitrin = window.vitrin == true;
        return !hasVitrin
          ? orderData.map((item: any) =>
              enrichProductWithTrackingNew({
                name: item.name,
                id: item.product_id,
                quantity: item.quantity,
                price: item.price || 0,
              }),
            )
          : orderData.items.map((item: any) =>
              enrichProductWithTrackingNew({
                name: item.item_name,
                id: item.item_id,
                quantity: item.quantity,
                price: item.price || 0,
              }),
            );
    }
  };

  const getAlfinderContribution = (purchasedProducts: any[]) => {
    // Get cart additions from localStorage with expiration handling
    const cartAdditions: CartAddition[] = getCartTrackingData();

    // Initialize contribution tracking
    const contribution = {
      recommendations: { count: 0, amount: 0 },
      search_bar: { count: 0, amount: 0 },
      search_page: { count: 0, amount: 0 },
      frequently_bought_together: { count: 0, amount: 0 },
      frequently_bought_together_modal: { count: 0, amount: 0 },
      total_alfinder_products: 0,
      total_alfinder_amount: 0,
    };

    // Match purchased products with cart additions
    purchasedProducts.forEach((product) => {
      // Find the first matching cart addition for this product (first-touch attribution)
      const firstAddition = cartAdditions.find(
        (addition) => addition.productName === product.name,
      );

      if (firstAddition) {
        const productTotal = (product.price || 0) * (product.quantity || 1);
        const productQuantity = product.quantity || 1;

        // Count: full quantity to the first source (integer, not fractional)
        // Amount: full amount to the first source
        if (contribution[firstAddition.source]) {
          contribution[firstAddition.source].count += productQuantity;
          contribution[firstAddition.source].amount += productTotal;
        }

        // Add to total alfinder contribution (only once per product)
        contribution.total_alfinder_products += productQuantity;
        contribution.total_alfinder_amount += productTotal;
      }
    });

    return contribution;
  };

  useEffect(() => {
    if (window.alfinderDataLayer) {
      let originalPush = window.alfinderDataLayer.push;
      window.alfinderDataLayer.push = function (...args) {
        const result = originalPush.apply(this, args);
        console.log('args', args);

        const alfinderDataLayerEvent = args[0];
        if (
          alfinderDataLayerEvent &&
          alfinderDataLayerEvent.event === 'customer::purchase'
        ) {
          const orderData = alfinderDataLayerEvent.data;
          console.log({ orderData });
          const purchasedProducts = getProducts(orderData);
          const purchasedProductsNew = getProductsNew(orderData);
          const totalOrderValue = getTotalOrderValue(orderData);
          const alfinderContribution =
            getAlfinderContribution(purchasedProducts);

          console.log('orderData', orderData);
          console.log('alfinder contribution', alfinderContribution);

          // Send purchasing_completed first
          safeTrack('purchasing_completed', {
            orderId:
              provider === 'salla'
                ? Number(orderData.order_id)
                : window.vitrin == true
                ? Number(orderData.order.id)
                : undefined,
            products: purchasedProducts,
            totalOrderValue,
            sallaOrderValue: provider === 'salla' ? orderData.total : undefined,
            storeId: store,
            provider,
            customerId: customer,
            recommendations_count: alfinderContribution.recommendations.count,
            recommendations_amount: alfinderContribution.recommendations.amount,
            search_bar_count: alfinderContribution.search_bar.count,
            search_bar_amount: alfinderContribution.search_bar.amount,
            search_page_count: alfinderContribution.search_page.count,
            search_page_amount: alfinderContribution.search_page.amount,
            frequently_bought_together_count:
              alfinderContribution.frequently_bought_together.count,
            frequently_bought_together_amount:
              alfinderContribution.frequently_bought_together.amount,
            frequently_bought_together_modal_count:
              alfinderContribution.frequently_bought_together_modal.count,
            frequently_bought_together_modal_amount:
              alfinderContribution.frequently_bought_together_modal.amount,
            total_alfinder_products:
              alfinderContribution.total_alfinder_products,
            total_alfinder_amount: alfinderContribution.total_alfinder_amount,
            contribution_percentage:
              totalOrderValue > 0
                ? (alfinderContribution.total_alfinder_amount /
                    totalOrderValue) *
                  100
                : 0,
          });

          // Wait a bit before sending the second event to avoid recursion issues

          safeTrack('order_completed', {
            ...orderData,
            orderRevenue: orderData.revenue ?? getTotalOrderValue(orderData),
            products: purchasedProductsNew,
          });

          // Clear cart additions after successful purchase tracking
          clearCartTracking();
        }

        return result;
      };
    }
  }, []);

  return <></>;
};

export default Purchase;
